// LevelBadge — a small pill in the topbar that shows the current user's
// level glyph, XP, streak, and (on hover) a mini progress ring to the next
// level. Polls every 60s so completing a learning across tabs updates
// without a manual refresh. Clicking it opens the Activity page.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Flame } from "lucide-react";

const POLL_MS = 60 * 1000;

export const LevelBadge = () => {
    const [stats, setStats] = useState(null);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const r = await api.get("/me/stats");
                if (alive) setStats(r.data);
            } catch { /* not signed in yet — fine */ }
        };
        load();
        const id = setInterval(load, POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, []);

    if (!stats?.level) return null;
    const { level, next_level, progress_pct, xp, to_next, current_streak } = stats;

    return (
        <Link
            to="/activity"
            data-testid="topbar-level-badge"
            className="relative inline-flex items-center gap-2 h-9 pl-1 pr-3 rounded-full bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-100 hover:border-[#2EB4AC]/40 hover:shadow-sm transition-all"
            onMouseEnter={() => setTooltipOpen(true)}
            onMouseLeave={() => setTooltipOpen(false)}
            title={`${level.label} · ${xp} XP${to_next ? ` · ${to_next} to ${next_level.label}` : ""}`}
        >
            {/* Circular level indicator */}
            <span className="relative h-7 w-7 rounded-full bg-white shadow-inner ring-1 ring-teal-100 flex items-center justify-center text-[14px]">
                {level.glyph}
                {next_level && (
                    <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 28 28" aria-hidden>
                        <circle cx="14" cy="14" r="12" fill="none" stroke="rgba(46,180,172,0.15)" strokeWidth="2" />
                        <circle
                            cx="14"
                            cy="14"
                            r="12"
                            fill="none"
                            stroke="#2EB4AC"
                            strokeWidth="2"
                            strokeDasharray={`${(progress_pct / 100) * 75.4} 75.4`}
                            strokeLinecap="round"
                        />
                    </svg>
                )}
            </span>
            <span className="hidden sm:flex flex-col items-start leading-tight">
                <span className="text-[10px] uppercase tracking-[0.08em] text-slate-500 font-semibold">{level.label}</span>
                <span className="text-[11px] font-bold text-[#1F7F79] tabular-nums">{xp} XP</span>
            </span>
            {current_streak > 0 && (
                <span
                    className="ml-0.5 inline-flex items-center gap-0.5 h-6 px-1.5 rounded-full bg-orange-50 border border-orange-100 text-[11px] font-bold text-orange-700"
                    title={`${current_streak}-day streak — one Odyssey action a day keeps it alive`}
                    aria-label={`${current_streak} day streak`}
                >
                    <Flame className="h-3 w-3 fill-orange-500 text-orange-500" />
                    <span className="tabular-nums">{current_streak}</span>
                </span>
            )}
            {tooltipOpen && (
                <div className="absolute top-full right-0 mt-2 z-50 w-64 bg-white rounded-xl shadow-xl ring-1 ring-slate-200 p-3 pointer-events-none">
                    <div className="text-xs font-semibold text-slate-900 flex items-center gap-1.5">
                        <span>{level.glyph}</span> {level.label}
                    </div>
                    {next_level ? (
                        <>
                            <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#2EB4AC] to-emerald-500" style={{ width: `${progress_pct}%` }} />
                            </div>
                            <div className="mt-1.5 text-[11px] text-slate-500">
                                <span className="font-semibold text-slate-700">{to_next} XP</span> to {next_level.glyph} {next_level.label}
                            </div>
                        </>
                    ) : (
                        <div className="mt-1 text-[11px] text-slate-500">You&apos;re at the top of the ladder — legendary!</div>
                    )}
                    <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-500 leading-snug">
                        Earn XP by completing learnings, posting notes, sharing news + reading team stories.
                    </div>
                </div>
            )}
        </Link>
    );
};

export default LevelBadge;
